import React, { useEffect } from 'react';
import { HStack, Button, VStack } from '@chakra-ui/react';
import { Colors } from 'core/ui';

type ButtonItem = {
	id: number;
	selected: boolean;
	Style: {
		Text: string;
		ImageId: string;
		BackColor: string;
	}
};

type Props = {
	onItemSelected: (id: number) => Promise<void>;
	items: Array<ButtonItem>;
	orientation?: 'horizontal' | 'vertical';
};

function LocationSelectorButton(props: { item: ButtonItem, onItemSelected: (id: number) => Promise<void>, width: string, height: string }): React.ReactNode {

	const { item, onItemSelected, width, height } = props;
	const backgroundColor = item.Style.BackColor ? Colors.getBackgroundColorAsRgb(item.Style.BackColor) : item.selected ? 'primary' : 'transparent';
	const hoverColor = item.Style.BackColor ? Colors.getBackgroundColorAsRgb(item.Style.BackColor) : item.selected ? 'primary' : 'primary._hover';
	const borderColor = item.Style.BackColor ? { base: item.selected ? '{colors.neutral.800}' : 'transparent', _dark: item.selected ? '{colors.neutral.500}' : 'transparent' } : { base: item.selected ? 'transparent' : '{colors.neutral.400}', _dark: item.selected ? 'transparent' : '{colors.neutral.500}' };
	const color = item.Style.BackColor ? Colors.getTextColor(backgroundColor) : { base: item.selected ? 'white' : '{colors.neutral.800}', _dark: item.selected ? 'white' : '{colors.neutral.500}' };

	return <Button
		id={item.id.toString()}
		bg={backgroundColor}
		_hover={{ bg: hoverColor, opacity: 1 }}
		w={width}
		h={height}
		borderWidth={item.Style.BackColor && item.selected ? '2px' : '1px'}
		borderColor={borderColor}
		shadow={'sm'}
		rounded={'md'}
		_focus={{ focusRing: 'none' }}
		fontSize={'sm'}
		fontWeight={'semibold'}
		color={color}
		whiteSpace={'normal'}
		overflow={'hidden'} /* Para evitar que si el texto es largo se salga del botón */
		onClick={() => onItemSelected(item.id)}
		display={'flex'}
		flexDirection={'column'}
		alignItems={'center'}
		justifyContent={'space-around'}
	>
		{item.Style.Text}
	</Button>;
}

function HLocationSelector({ items, onItemSelected }: Props): React.ReactNode {
	return <HStack maxW={'full'} gap={'5'} rounded={'md'} overflow={'auto'}>
		{items.map((item, idx) =>
			<LocationSelectorButton key={idx} item={item} onItemSelected={onItemSelected} width={'20'} height={'16'} />
		)}
	</HStack>;
}

function VLocationSelector({ items, onItemSelected }: Props): React.ReactNode {
	return <VStack w={'full'} height={'full'} gap={'4'} rounded={'md'} overflowY={'auto'} overflowX={'hidden'}>
		{items.map((item, idx) =>
			<LocationSelectorButton key={idx} item={item} onItemSelected={onItemSelected} width={'20'} height={'16'} />
		)}
	</VStack>;
}

export default function LocationSelector(props: Props): React.ReactNode {
	const { items, onItemSelected, orientation = 'horizontal' } = props;

	useEffect(() => {
		const selected = items.filter(x => x.selected);
		selected && scrollToElement(selected[0].id.toString());
	}, [items]);

	const scrollToElement = (elementId: string) => {
		const element = document.getElementById(elementId);
		element && element.scrollIntoView({ block: 'center' });
	};

	return (
		orientation === 'horizontal'
			? <HLocationSelector items={items} onItemSelected={onItemSelected} orientation={orientation} />
			: <VLocationSelector items={items} onItemSelected={onItemSelected} orientation={orientation} />
	);
}
